import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronUp, Clock, Edit, MoreHorizontal, Trash2, Users } from "lucide-react";
import { LGoBadge, LGoButton, LGoCard, LGoCardContent } from "@/ui/branded";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/ui/primitives/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/ui/primitives/dropdown-menu";
import { OfferReservationsList } from "./OfferReservationsList";
import { tradingPendingBadge } from "./TradingPendingNotice";
import type { PartnerOffer } from "@/domain/offer/types";
import type { SaleStanding } from "@/domain/offer/rules";
import { offerStage, priceDiscountPercentage, saleStanding } from "@/domain/offer/rules";
import { occupancySentence } from "@/lib/czech";
import { formatDate, formatTime } from "@/lib/time";
import { prefersReducedMotion } from "@/lib/motion";
import { cn } from "@/lib/cn";

interface OfferRowProps {
  offer: PartnerOffer;
  canTrade: boolean;
  isHighlighted?: boolean;
  now: Date;
  onEdit: (offer: PartnerOffer) => void;
  onCancel: (offer: PartnerOffer) => void;
}

const stageBadge = {
  ended: { label: "Ukončená", tone: "neutral" as const },
  running: { label: "Probíhá", tone: "success" as const },
  scheduled: { label: "Naplánovaná", tone: "outline" as const },
};

function sameCalendarDay(instant: Date, other: Date): boolean {
  return formatDate(instant, "yyyy-MM-dd") === formatDate(other, "yyyy-MM-dd");
}

function saleClock(instant: Date, now: Date): string {
  const time = formatTime(instant);
  return sameCalendarDay(instant, now) ? time : `${formatDate(instant, "d. M.")} ${time}`;
}

function saleClockWithToday(instant: Date, now: Date): string {
  const clock = saleClock(instant, now);
  return sameCalendarDay(instant, now) ? `dnes ${clock}` : clock;
}

/** Says only what the authored end holds: when selling stopped, or when it is
 *  set to. A window the partner left open belongs to the API's defaults, so the
 *  card stays quiet rather than guessing whether anyone can buy. The pointer to
 *  the setting is worth a line only once the partner is asking why it stopped. */
function saleWindowLine(standing: SaleStanding, now: Date): string | null {
  if (standing.kind === "unstated") return null;
  if (standing.kind === "closed")
    return `Prodej skončil ${saleClockWithToday(standing.endsAt, now)} — podle pole „Konec prodeje“ v úpravě slevového termínu.`;
  return `Prodej do ${saleClock(standing.endsAt, now)}`;
}

export function OfferRow({
  offer,
  canTrade,
  isHighlighted = false,
  now,
  onEdit,
  onCancel,
}: OfferRowProps) {
  const [isShowingReservations, setIsShowingReservations] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const stage = offerStage(offer.startTime, offer.endTime, now);
  const status = stageBadge[stage];
  const discount =
    offer.priceOriginal != null && offer.priceDiscounted != null
      ? priceDiscountPercentage(offer.priceOriginal, offer.priceDiscounted)
      : null;
  const standing = saleStanding(offer.saleWindow, now);
  const hasStoppedSelling = standing.kind === "closed";
  const saleLine = stage === "ended" ? null : saleWindowLine(standing, now);
  const occupancy = occupancySentence(offer.reserved, offer.capacity);

  // Landing here from Dnes must read as "you arrived", not "the page moved" —
  // the scroll plus a marking that stays for as long as `?offer=` names this
  // row, not a highlight that fades before the eye catches it.
  useEffect(() => {
    if (isHighlighted)
      cardRef.current?.scrollIntoView({
        behavior: prefersReducedMotion() ? "auto" : "smooth",
        block: "center",
      });
  }, [isHighlighted]);

  return (
    <Collapsible open={isShowingReservations} onOpenChange={setIsShowingReservations}>
      <LGoCard
        ref={cardRef}
        variant="flat"
        className={cn(
          "hover:border-primary/40 transition-colors duration-base ease-out-expo",
          isHighlighted && "ring-2 ring-primary ring-offset-2 ring-offset-background bg-primary/5",
        )}
      >
        <LGoCardContent className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 space-y-3">
              <div className="flex items-center gap-3 flex-wrap">
                <h3 className="font-semibold text-lg">{offer.activityName}</h3>
                <LGoBadge variant={status.tone}>{status.label}</LGoBadge>
                {discount && discount > 0 && <LGoBadge variant="gold">−{discount}%</LGoBadge>}
                {hasStoppedSelling && stage !== "ended" && (
                  <LGoBadge variant="neutral">Neprodává se</LGoBadge>
                )}
                {!canTrade && <LGoBadge variant="neutral">{tradingPendingBadge}</LGoBadge>}
              </div>

              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <Clock className="h-4 w-4" aria-hidden="true" />
                  <span>
                    {formatDate(offer.startTime, "HH:mm")} – {formatDate(offer.endTime, "HH:mm")}
                  </span>
                </div>
                <CollapsibleTrigger asChild>
                  <LGoButton
                    variant="ghost"
                    className="h-11 gap-1.5 px-0 text-sm font-normal text-muted-foreground underline underline-offset-4 hover:bg-transparent hover:text-foreground"
                    aria-label={`${occupancy} — ${
                      isShowingReservations ? "skrýt rezervace" : "zobrazit rezervace"
                    }`}
                  >
                    <Users aria-hidden="true" />
                    {occupancy}
                    {isShowingReservations ? (
                      <ChevronUp aria-hidden="true" />
                    ) : (
                      <ChevronDown aria-hidden="true" />
                    )}
                  </LGoButton>
                </CollapsibleTrigger>
              </div>

              <div className="flex items-center gap-3">
                {offer.priceOriginal != null && (
                  <span className="text-muted-foreground line-through">
                    {offer.priceOriginal} Kč
                  </span>
                )}
                {offer.priceDiscounted != null && (
                  <span className="text-lg font-bold text-primary">
                    {offer.priceDiscounted} Kč
                  </span>
                )}
              </div>

              {saleLine && <p className="text-sm text-muted-foreground">{saleLine}</p>}
            </div>

            <div className="flex shrink-0 items-center gap-2">
              <LGoButton
                variant="secondary"
                onClick={() => onEdit(offer)}
                disabled={stage === "ended"}
                className="h-11 px-4"
                aria-label="Upravit slevový termín"
                iconLeft={<Edit aria-hidden="true" />}
              >
                Upravit
              </LGoButton>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <LGoButton variant="ghost" className="h-11 w-11 p-0" aria-label="Další akce">
                    <MoreHorizontal aria-hidden="true" />
                  </LGoButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem asChild className="min-h-11">
                    <Link to={`/reservations?offer=${offer.id}`}>
                      <Users className="mr-2 h-4 w-4" aria-hidden="true" />
                      Zobrazit rezervace
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => onCancel(offer)}
                    className="min-h-11 text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
                    Zrušit termín
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          <CollapsibleContent className="mt-4 pt-4 border-t border-border">
            <OfferReservationsList offer={offer} now={now} />
          </CollapsibleContent>
        </LGoCardContent>
      </LGoCard>
    </Collapsible>
  );
}
